import React, { useEffect, useMemo, useState } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import Table from '../components/table/Table';
import Badge from '../components/badge/Badge';
import { fetchRegularInspections, rescheduleRegularInspection } from '../lib/crmClient';
import { getEmployeeByEmail, isAdminEmail, TECHNICI } from '../lib/technicians';

const resolvedVfs =
  (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ||
  (pdfFonts && pdfFonts.vfs) ||
  pdfFonts;

if (resolvedVfs) {
  pdfMake.vfs = resolvedVfs;
}

pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
};

const formatDate = (value) => {
  if (!value) return '-';
  const raw = String(value).slice(0, 10);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value);
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
};

const normalizeStatusKey = (value) => String(value || '').trim().toLowerCase();

const statusToBadge = (value) => {
  const text = normalizeStatusKey(value);
  if (text.includes('zpo')) return 'danger';
  if (text.includes('napl')) return 'success';
  return 'warning';
};

const statusForPdf = (value) => String(value || '-').trim() || '-';

const todayIso = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const normalizePhone = (value) => String(value || '').trim();

const matchesTechnician = (item, technician) => {
  if (!technician) return true;

  const itemEmployeeId = String(item && item.ZamestnanecId ? item.ZamestnanecId : '').trim();
  const itemEmployeeEmail = normalizeEmail(item && item.ZamestnanecEmail);
  const wantedId = String(technician.id || '').trim();
  const wantedEmail = normalizeEmail(technician.email);

  return (wantedId && itemEmployeeId === wantedId) || (wantedEmail && itemEmployeeEmail === wantedEmail);
};

const PravidelneKontroly = ({ email }) => {
  const canViewAll = useMemo(() => isAdminEmail(email), [email]);
  const ownEmployee = useMemo(() => getEmployeeByEmail(email), [email]);
  const ownEmployeeId = ownEmployee ? String(ownEmployee.id) : 'all';
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  const [rows, setRows] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTechnicianId, setSelectedTechnicianId] = useState(ownEmployeeId);
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEanCheck, setSelectedEanCheck] = useState(null);
  const [plannedDate, setPlannedDate] = useState(todayIso());
  const [rescheduledEmployeeId, setRescheduledEmployeeId] = useState('');
  const [customerActive, setCustomerActive] = useState(true);
  const [savingEanCheck, setSavingEanCheck] = useState(false);
  const [eanCheckError, setEanCheckError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!canViewAll) {
      setSelectedTechnicianId(ownEmployeeId);
      return;
    }
    if (!selectedTechnicianId || selectedTechnicianId === 'all') {
      setSelectedTechnicianId(ownEmployeeId);
    }
  }, [canViewAll, ownEmployeeId, selectedTechnicianId]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const apiStatus = statusFilter === 'all' ? '' : statusFilter;
        const selectedTechnicianForApi = TECHNICI.find((item) => String(item.id) === String(selectedTechnicianId)) || null;
        const requestedEmail = canViewAll
          ? (selectedTechnicianForApi ? selectedTechnicianForApi.email : email)
          : email;

        const data = await fetchRegularInspections({
          email: requestedEmail,
          status: apiStatus,
          fromDate,
          toDate,
          signal: controller.signal,
        });

        if (!cancelled) {
          setRows(data);
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        if (!cancelled) {
          setRows([]);
          setError(e instanceof Error ? e.message : 'Načtení pravidelných kontrol selhalo');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [email, canViewAll, selectedTechnicianId, statusFilter, fromDate, toDate, refreshKey]);

  const selectedTechnician = useMemo(() => {
    if (!canViewAll) return ownEmployee;
    if (selectedTechnicianId === 'all') return null;
    return TECHNICI.find((item) => String(item.id) === String(selectedTechnicianId)) || null;
  }, [canViewAll, ownEmployee, selectedTechnicianId]);

  const selectedTechnicianLabel = useMemo(() => {
    if (!canViewAll) {
      return ownEmployee ? ownEmployee.fullName : String(email || '').trim();
    }
    if (!selectedTechnician) return 'Všichni technici';
    return selectedTechnician.fullName;
  }, [canViewAll, ownEmployee, email, selectedTechnician]);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();

    return rows
      .filter((item) => {
        if (canViewAll) {
          if (selectedTechnician && !matchesTechnician(item, selectedTechnician)) return false;
        } else {
          const ownTech = ownEmployee || { email };
          if (!matchesTechnician(item, ownTech)) return false;
        }

        if (statusFilter !== 'all') {
          const matchStatusByNum = String(item.Status || '') === String(statusFilter);
          const text = normalizeStatusKey(item.StatusKontroly);
          const matchStatusByText = statusFilter === '1' ? text.includes('napl') : text.includes('zpo');
          if (!matchStatusByNum && !matchStatusByText) return false;
        }

        if (fromDate || toDate) {
          const value = item.NovePlanovanaKontrolaDatum;
          if (!value) return false;
          const date = String(value).slice(0, 10);
          if (fromDate && date < fromDate) return false;
          if (toDate && date > toDate) return false;
        }

        if (!q) return true;

        const text = [
          item.ZakaznikNazev,
          item.Ulice,
          item.Mesto,
          item.KontaktniOsoba,
          item.Telefon,
          item.Email,
          item.Poznamka,
          item.PravidelnostNazev,
          item.NovePlanovanaKontrolaDatum,
          item.StatusKontroly,
        ]
          .filter(Boolean)
          .join(' | ')
          .toLowerCase();

        return text.includes(q);
      })
      .sort((a, b) => {
        const ad = new Date(a.NovePlanovanaKontrolaDatum || 0).getTime();
        const bd = new Date(b.NovePlanovanaKontrolaDatum || 0).getTime();
        return ad - bd;
      });
  }, [rows, search, statusFilter, fromDate, toDate, canViewAll, selectedTechnician, ownEmployee, email]);

  const summary = useMemo(() => {
    const planned = filtered.filter((item) => normalizeStatusKey(item.StatusKontroly).includes('napl')).length;
    const delayed = filtered.filter((item) => normalizeStatusKey(item.StatusKontroly).includes('zpo')).length;
    return {
      total: filtered.length,
      planned,
      delayed,
    };
  }, [filtered]);

  const openReschedule = (item) => {
    setSelectedEanCheck(item);
    setPlannedDate(item.NovePlanovanaKontrolaDatum || todayIso());
    setRescheduledEmployeeId(item.ZamestnanecId === null || item.ZamestnanecId === undefined ? '' : String(item.ZamestnanecId));
    setCustomerActive(item.Aktivni !== false);
    setEanCheckError('');
    setNotice('');
  };

  const handleReschedule = async (event) => {
    event.preventDefault();
    if (!selectedEanCheck || savingEanCheck) return;

    setSavingEanCheck(true);
    setEanCheckError('');
    try {
      await rescheduleRegularInspection({
        ZakaznikId: selectedEanCheck.ZakaznikId,
        EAN: selectedEanCheck.EAN ?? null,
        PlannedDate: plannedDate,
        ZamestnanecId: rescheduledEmployeeId || null,
        Aktivni: customerActive,
      });
      setSelectedEanCheck(null);
      setNotice(`Změny pro ${selectedEanCheck.ZakaznikNazev || 'zákazníka'} byly uloženy. Kontrola je naplánována na ${formatDate(plannedDate)}.`);
      setRefreshKey((value) => value + 1);
    } catch (saveError) {
      setEanCheckError(saveError instanceof Error ? saveError.message : 'Záznam EAN scanu se nepodařilo uložit.');
    } finally {
      setSavingEanCheck(false);
    }
  };

  const handleExportPdf = () => {
    const generatedAt = new Date().toLocaleString('cs-CZ');
    const reportTitle = `Pravidelné kontroly - ${selectedTechnicianLabel}`;

    const tableRows = filtered.map((item) => [
      item.ZakaznikNazev || '-',
      item.Ulice || '-',
      formatDate(item.NovePlanovanaKontrolaDatum),
      statusForPdf(item.StatusKontroly),
      item.PravidelnostNazev || '-',
      item.KontaktniOsoba || '-',
      normalizePhone(item.Telefon) || '-',
    ]);

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [10, 8, 10, 10],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 9,
      },
      footer: (currentPage, pageCount) => {
        if (currentPage !== pageCount) {
          return { text: '' };
        }
        return {
          columns: [
            { text: '' },
            {
              text: `Vygenerováno: ${generatedAt}`,
              alignment: 'right',
              margin: [0, 0, 10, 4],
              fontSize: 8,
              color: '#616161',
            },
          ],
        };
      },
      content: [
        {
          table: {
            widths: ['*'],
            body: [[{ text: reportTitle, style: 'titleBar' }]],
          },
          layout: 'noBorders',
          margin: [0, 0, 0, 2],
        },
        {
          text: `Technik: ${selectedTechnicianLabel}`,
          style: 'subHeader',
          margin: [2, 0, 0, 6],
        },
        {
          table: {
            widths: ['*', '*', '*'],
            body: [
              [
                { text: 'Celkem', style: 'summaryLabelCenter' },
                { text: 'Naplánované', style: 'summaryLabelCenter' },
                { text: 'Zpožděné', style: 'summaryLabelCenter' },
              ],
              [
                { text: String(summary.total), style: 'summaryValueCenter' },
                { text: String(summary.planned), style: 'summaryValueCenter' },
                { text: String(summary.delayed), style: 'summaryValueCenter' },
              ],
            ],
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? '#f7f7f7' : null),
            hLineColor: () => '#dddddd',
            vLineColor: () => '#dddddd',
          },
          margin: [0, 0, 0, 6],
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Zákazník', style: 'thLeft' },
                { text: 'Ulice', style: 'thLeft' },
                { text: 'Plánovaná kontrola', style: 'thLeft' },
                { text: 'Status', style: 'thLeft' },
                { text: 'Pravidelnost', style: 'thLeft' },
                { text: 'Kontaktní osoba', style: 'thLeft' },
                { text: 'Telefon', style: 'thLeft' },
              ],
              ...tableRows.map((row) => ([
                { text: row[0], style: 'tdLeft' },
                { text: row[1], style: 'tdLeft' },
                { text: row[2], style: 'tdLeft' },
                { text: row[3], style: 'tdLeft' },
                { text: row[4], style: 'tdLeft' },
                { text: row[5], style: 'tdLeft' },
                { text: row[6], style: 'tdLeft' },
              ])),
            ],
          },
          layout: {
            fillColor: (rowIndex) => {
              if (rowIndex === 0) return '#d68102';
              return rowIndex % 2 === 0 ? '#fafafa' : null;
            },
            hLineColor: () => '#e0e0e0',
            vLineColor: () => '#e0e0e0',
          },
        },
      ],
      styles: {
        titleBar: {
          color: '#ffffff',
          fillColor: '#d68102',
          bold: true,
          fontSize: 13,
          margin: [8, 5, 8, 5],
        },
        summaryLabelCenter: {
          bold: true,
          fontSize: 8.5,
          alignment: 'center',
        },
        summaryValueCenter: {
          alignment: 'center',
          fontSize: 9,
          bold: true,
        },
        thLeft: {
          color: '#ffffff',
          bold: true,
          fontSize: 8.5,
          alignment: 'left',
        },
        tdLeft: {
          fontSize: 8.5,
          alignment: 'left',
        },
        subHeader: {
          fontSize: 9,
          bold: true,
          color: '#424242',
        },
      },
    };

    pdfMake.createPdf(docDefinition).download('pravidelne-kontroly.pdf');
  };

  return (
    <div>
      <h2 className="page-header">Pravidelné kontroly</h2>

      <div className="card">
        <div className="crm-toolbar crm-toolbar--kontroly">
          <input
            placeholder="Hledat zákazníka, ulici, kontakt, telefon..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSearch(searchInput.trim());
            }}
          />

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Všechny statusy</option>
            <option value="1">Naplánovaná</option>
            <option value="0">Zpožděná</option>
          </select>

          {canViewAll ? (
            <select value={selectedTechnicianId} onChange={(e) => setSelectedTechnicianId(e.target.value)}>
              <option value="all">Všichni technici</option>
              {TECHNICI.map((item) => (
                <option key={item.id} value={item.id}>{item.fullName}</option>
              ))}
            </select>
          ) : (
            <input value={selectedTechnicianLabel} disabled />
          )}

          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />

          <button type="button" className="btn btn-primary" onClick={() => setSearch(searchInput.trim())}>
            Hledat
          </button>

          <button type="button" className="btn btn-blue" onClick={handleExportPdf}>
            Export PDF
          </button>
        </div>

        <div className="crm-summary-grid crm-summary-grid--3">
          <div className="crm-summary-card">
            <p>Celkem kontrol</p>
            <strong>{summary.total}</strong>
          </div>
          <div className="crm-summary-card">
            <p>Naplánované</p>
            <strong>{summary.planned}</strong>
          </div>
          <div className="crm-summary-card">
            <p>Zpožděné</p>
            <strong>{summary.delayed}</strong>
          </div>
        </div>

        <p className="crm-form-note" style={{ marginBottom: 10 }}>
          Technik: <strong>{selectedTechnicianLabel}</strong>
        </p>

        {error ? <p className="crm-error" style={{ marginTop: 15 }}>{error}</p> : null}
        {notice ? <p className="crm-success" role="status" style={{ marginTop: 15 }}>{notice}</p> : null}
        {loading ? <p style={{ marginTop: 15 }}>Načítám...</p> : null}

        <Table
          headData={isMobile ? ['Kontrola'] : ['Zákazník', 'Ulice', 'Plánovaná kontrola', 'Status', 'Pravidelnost', 'Kontaktní osoba', 'Telefon', 'Akce']}
          renderHead={(item, index) => (
            <th key={index} style={{ fontSize: '0.86rem', padding: '10px 8px' }}>{item}</th>
          )}
          bodyData={filtered}
          limit={50}
          renderBody={(item, index) => (
            <tr key={`${item.ZakaznikId || 'z'}-${item.NovePlanovanaKontrolaDatum || 'd'}-${index}`}>
              {isMobile ? (
                <td>
                  <div className="crm-mobile-stack">
                    <div className="crm-mobile-stack__header">
                      <div className="crm-mobile-stack__title-group">
                        <span className="crm-mobile-stack__eyebrow">{formatDate(item.NovePlanovanaKontrolaDatum)}</span>
                        <span className="crm-mobile-stack__title">{item.ZakaznikNazev || '-'}</span>
                      </div>
                      <Badge type={statusToBadge(item.StatusKontroly)} content={item.StatusKontroly || '-'} />
                    </div>
                    <span className="crm-mobile-stack__line">{item.Ulice || '-'}</span>
                    <span className="crm-mobile-stack__line">EAN scan: {formatDate(item.PosledniEANCheckTimeStamp)}</span>
                    <span className="crm-mobile-stack__line">{item.KontaktniOsoba || normalizePhone(item.Telefon) || '-'}</span>
                    <div className="crm-mobile-stack__actions">
                      <span className="crm-mobile-stack__line">{item.PravidelnostNazev || '-'}</span>
                      <span className="crm-mobile-stack__value">{normalizePhone(item.Telefon) || '-'}</span>
                    </div>
                    <button type="button" className="btn btn-blue" onClick={() => openReschedule(item)}>Upravit</button>
                  </div>
                </td>
              ) : (
                <>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px' }}>
                    <div>{item.ZakaznikNazev || '-'}</div>
                    <small>EAN scan: {formatDate(item.PosledniEANCheckTimeStamp)}</small>
                  </td>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px' }}>{item.Ulice || '-'}</td>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatDate(item.NovePlanovanaKontrolaDatum)}</td>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px' }}>
                    <Badge type={statusToBadge(item.StatusKontroly)} content={item.StatusKontroly || '-'} />
                  </td>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px' }}>{item.PravidelnostNazev || '-'}</td>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px' }}>{item.KontaktniOsoba || '-'}</td>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{normalizePhone(item.Telefon) || '-'}</td>
                  <td style={{ fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn btn-blue" onClick={() => openReschedule(item)}>Upravit</button>
                  </td>
                </>
              )}
            </tr>
          )}
        />
      </div>
      {selectedEanCheck ? (
        <div
          className="crm-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={`Upravit kontrolu pro ${selectedEanCheck.ZakaznikNazev || 'zákazníka'}`}
          onClick={(event) => {
            if (event.target === event.currentTarget && !savingEanCheck) setSelectedEanCheck(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !savingEanCheck) setSelectedEanCheck(null);
          }}
        >
          <form className="crm-modal-card" onSubmit={handleReschedule}>
            <div className="crm-modal-header">
              <div>
                <h3>Upravit kontrolu</h3>
                <p>{selectedEanCheck.ZakaznikNazev || 'Zákazník'}</p>
              </div>
              <button
                type="button"
                className="crm-modal-close-btn"
                aria-label="Zavřít"
                disabled={savingEanCheck}
                onClick={() => setSelectedEanCheck(null)}
              ><i className="bx bx-x" /></button>
            </div>
            <label htmlFor="manual-ean-date">Požadovaný termín kontroly</label>
            <input
              id="manual-ean-date"
              className="crm-modal-search"
              type="date"
              min={todayIso()}
              required
              value={plannedDate}
              onChange={(event) => setPlannedDate(event.target.value)}
            />
            <label htmlFor="reschedule-technician">Technik</label>
            <select
              id="reschedule-technician"
              className="crm-modal-search"
              value={rescheduledEmployeeId}
              onChange={(event) => setRescheduledEmployeeId(event.target.value)}
            >
              <option value="">Bez přiřazení</option>
              {selectedEanCheck.ZamestnanecId && !TECHNICI.some((item) => String(item.id) === String(selectedEanCheck.ZamestnanecId)) ? (
                <option value={String(selectedEanCheck.ZamestnanecId)}>Stávající technik (ID {selectedEanCheck.ZamestnanecId})</option>
              ) : null}
              {TECHNICI.map((item) => <option key={item.id} value={item.id}>{item.fullName}</option>)}
            </select>
            <label htmlFor="reschedule-customer-active">Stav zákazníka</label>
            <select
              id="reschedule-customer-active"
              className="crm-modal-search"
              value={customerActive ? 'true' : 'false'}
              onChange={(event) => setCustomerActive(event.target.value === 'true')}
            >
              <option value="true">Aktivní</option>
              <option value="false">Neaktivní</option>
            </select>
            {eanCheckError ? <p className="crm-error" role="alert">{eanCheckError}</p> : null}
            <div className="crm-modal-header-actions">
              <button type="button" className="btn" disabled={savingEanCheck} onClick={() => setSelectedEanCheck(null)}>Zrušit</button>
              <button type="submit" className="btn btn-primary" disabled={savingEanCheck || !plannedDate}>
                {savingEanCheck ? 'Ukládám...' : 'Uložit změny'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default PravidelneKontroly;
